import React, { useEffect, useState } from 'react';
import { vaultsService } from '@app/services';
import { passlyFormatDate, store, DATE_TIME_FORMAT } from '@app/helpers';
import { IVaultDetail, IUserData } from '@app/models';
import { Grid, Typography, Box } from '@material-ui/core';
import { PasslyTextField } from '@app/components';
import { PasslyAutoComplete } from '@app/componentsV2';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';

interface IProps {
    vault: IVaultDetail;
    passwordPolicies: any;
    isIndividual: boolean;
    setVaultName: Function;
}

interface EditableVaultData {
    DisplayName: string;
    Description: string;
    PasswordServerPolicyId: string;
    OnBehalfOfOrgId: string;
    [propName: string]: any;
}

const VaultInformation: React.SFC<IProps> = ({ vault, passwordPolicies, isIndividual, setVaultName }) => {
    const displayNameString = 'DisplayName';

    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();
    const [editData, setEditData] = useState<EditableVaultData>({} as EditableVaultData);
    const [displayNameError, setDisplayNameError] = useState('');
    const [currentVaultName, setCurrentVaultName] = useState('');
    const [lastModified, setLastModified] = useState<Date>(vault.LastModified);
    const [lastModifiedBy, setLastModifiedBy] = useState<string>(vault.LastModifiedBy);
    const availableOrgs = useSelector((state: any) => state.basic.shellConfig.organizations);
    const currentUser: IUserData = store.getState().signIn.currentUser as IUserData;

    useEffect(() => {
        let localEditData: EditableVaultData = {
            DisplayName: vault.DisplayName,
            Description: vault.Description,
            PasswordServerPolicyId: vault.PasswordServerPolicyId,
            OnBehalfOfOrgId: vault.OnBehalfOfOrgId
        };

        setCurrentVaultName(vault.DisplayName);
        setEditData(localEditData);
        setLastModified(vault.LastModified);
        setLastModifiedBy(vault.LastModifiedBy);
    }, [vault]);

    useEffect(() => {
        if (editData.DisplayName === '') {
            setDisplayNameError(translation('REQUIRED'));
        } else {
            setDisplayNameError('');
        }
    }, [editData.DisplayName]);

    const handleDataChange = (e: any) => {
        const { name, value } = e.target;
        let newData = { ...editData };

        newData[name] = value;
        setEditData(newData);
    };

    const handleSelectDataChange = (e: any) => {
        const { name, value } = e.target;
        let newData = { ...editData };

        newData[name] = value.code || value.id;
        setEditData(newData);
    };

    const handleSaveData = (e: any, name: string) => {
        if (name === displayNameString) {
            if (currentVaultName !== editData[name]) {
                const isVaultNameAvailableObject = {
                    displayName: editData[name],
                    tenantId: null,
                    onBehalfOfOrgId: editData.OnBehalfOfOrgId
                };

                vaultsService.isVaultNameAvailable(isVaultNameAvailableObject, handleError, handleSuccessVaultName);
            }

            return;
        }

        const patchObject = { [name]: editData[name] };

        vaultsService.patchVault(patchObject, vault.Id, onPatchSuccess.bind(null, name), handleError.bind(null, name));
    };

    const handleSaveEditableTextField = (name: string) => (value: string) => {
        if (name === displayNameString) {
            if (currentVaultName !== value) {
                const isVaultNameAvailableObject = {
                    displayName: value,
                    tenantId: null,
                    onBehalfOfOrgId: editData.OnBehalfOfOrgId
                };
                let newData = { ...editData };

                newData[name] = value;
                setEditData(newData);

                vaultsService.isVaultNameAvailable(isVaultNameAvailableObject, handleError, handleSuccessVaultName);
            }

            return;
        }

        const patchObject = { [name]: value };

        vaultsService.patchVault(patchObject, vault.Id, onPatchSuccess.bind(null, name), handleError.bind(null, name));
    };

    const handleDiscardData = (e: any, name: string) => {
        setEditData((prevState) => ({ ...prevState, [name]: vault[name] }));
    };

    const handleError = (name: string) => {
        setEditData((prevState) => ({ ...prevState, [name]: vault[name] }));
        dispatch(
            handleSetSnackbar({
                show: true,
                message: 'An error occurred while trying to update the field',
                severity: 'error'
            })
        );
    };

    const onPatchSuccess = (boundData: any) => {
        if (boundData === displayNameString) {
            setVaultName(editData.DisplayName);
            setCurrentVaultName(editData.DisplayName);
        }

        setLastModified(new Date());
        setLastModifiedBy(currentUser.username);

        dispatch(
            handleSetSnackbar({
                show: true,
                message: translation('SUCCESS_UPDATE_FIELD'),
                severity: 'success'
            })
        );
    };

    const handleSuccessVaultName = (response: any) => {
        if (response.data.value) {
            const patchObject = { DisplayName: editData.DisplayName };

            vaultsService.patchVault(patchObject, vault.Id, onPatchSuccess.bind(null, displayNameString), handleError);
        } else {
            setDisplayNameError(translation('INVALID_TAKEN_VAULT_NAME'));
        }
    };

    const getListDetail = (name: string, value: string) => {
        return (
            <Grid container direction='row' justifyContent='flex-start' alignItems='stretch' wrap='nowrap' spacing={3}>
                <Grid item>
                    <Box width={150}>
                        <Typography variant='body2'>{name}:</Typography>
                    </Box>
                </Grid>
                <Grid item>
                    <Typography noWrap variant='body1'>
                        {value}
                    </Typography>
                </Grid>
            </Grid>
        );
    };

    return (
        <Box m={2}>
            {editData.PasswordServerPolicyId !== undefined && (
                <>
                    <Grid container spacing={1} direction='column' justifyContent='flex-start' alignItems='stretch' wrap='nowrap'>
                        <Grid item xs={10} md={5}>
                            <Typography noWrap variant='body1'>
                                {`${translation('VAULT_ID')}: ${vault.Id}`}
                            </Typography>
                        </Grid>
                        <Grid item xs={10} md={5}>
                            <PasslyTextField
                                fullWidth
                                inlineedit
                                onClickSave={handleSaveData}
                                onClickDiscard={handleDiscardData}
                                editdisabled={editData.DisplayName === ''}
                                label={translation('VAULT_NAME')}
                                id='vaultName-input'
                                name='DisplayName'
                                buttonWidth={26}
                                buttonFontSize={'medium'}
                                onChange={handleDataChange}
                                type={'text'}
                                value={editData.DisplayName}
                                inputProps={{ maxLength: 100 }}
                                error={displayNameError !== ''}
                                helperText={displayNameError !== '' ? displayNameError : ''}
                            />
                        </Grid>

                        <Grid item xs={10} md={5}>
                            <PasslyTextField
                                fullWidth
                                inlineedit
                                onClickSave={handleSaveData}
                                onClickDiscard={handleDiscardData}
                                label={translation('DESCRIPTION')}
                                id='vaultDescription-input'
                                name='Description'
                                buttonWidth={26}
                                buttonFontSize={'medium'}
                                onChange={handleDataChange}
                                type={'text'}
                                value={editData.Description}
                                inputProps={{ maxLength: 500 }}
                            />
                        </Grid>

                        {!isIndividual && (
                            <Grid item xs={8} md={4}>
                                <PasslyAutoComplete
                                    inlineedit
                                    items={passwordPolicies}
                                    onChange={handleSelectDataChange}
                                    id='passwordServerPolicyId-input'
                                    name='PasswordServerPolicyId'
                                    label={translation('VAULT_PASSWORD_POLICY')}
                                    value={editData.PasswordServerPolicyId}
                                    buttonWidth={26}
                                    buttonFontSize={'medium'}
                                    editdisabled={editData.PasswordServerPolicyId === ''}
                                    onClickSave={handleSaveData}
                                    onClickDiscard={handleDiscardData}
                                    dropdown={true}
                                />
                            </Grid>
                        )}
                        {!isIndividual && (
                            <Grid item xs={8} md={4}>
                                <PasslyAutoComplete
                                    inlineedit
                                    items={availableOrgs}
                                    onChange={handleSelectDataChange}
                                    id='onBehalfOfOrgId-input'
                                    name='OnBehalfOfOrgId'
                                    label={translation('ORGANIZATION')}
                                    value={editData.OnBehalfOfOrgId}
                                    buttonWidth={26}
                                    buttonFontSize={'medium'}
                                    editdisabled={editData.OnBehalfOfOrgId === ''}
                                    onClickSave={handleSaveData}
                                    onClickDiscard={handleDiscardData}
                                    dropdown={true}
                                    valueField='id'
                                    appendTo={document.body}
                                />
                            </Grid>
                        )}
                    </Grid>
                    <Grid container direction='column' justifyContent='flex-start' alignItems='flex-start'>
                        {getListDetail(translation('TYPE'), vault.Owner === null ? translation('SHARED') : translation('INDIVIDUAL'))}
                        {vault.Owner === null ? '' : getListDetail(translation('OWNER'), vault.Owner.PrincipalName)}
                        {getListDetail(translation('VAULTS_LAST_MODIFIED'), passlyFormatDate(lastModified, DATE_TIME_FORMAT))}
                        {getListDetail(translation('LAST_MODIFIED_BY'), lastModifiedBy)}
                        {vault.LastExportedAt &&
                            getListDetail(translation('VAULT_LAST_EXPORTED'), passlyFormatDate(vault.LastExportedAt, DATE_TIME_FORMAT))}
                        {vault.LastExportedBy && getListDetail(translation('VAULT_LAST_EXPORTED_BY'), vault.LastExportedBy)}
                    </Grid>
                </>
            )}
        </Box>
    );
};

export default VaultInformation;
